<template>
  <section class="posts profile-content">
    <p v-if="status === 'error' && page === 1">Произошла ошибка при загрузке статей.</p>

    <div
      v-else-if="items.length === 0 && !total"
      class="posts__no-data"
    >
      <BannerNoData
        title="Нет статей"
        :text="isOwnProfile ? 'Напишите первую!' : undefined"
      />
    </div>

    <div
      v-else
      class="posts__list"
    >
      <div
        v-for="(article, index) in items"
        :key="article.id"
        class="posts__item"
      >
        <ArticleCardSmall
          v-bind="useArticleProps(article)"
          :loading-views="getLoadingViews(article.id)"
          cover-size="small"
          @click-on-article-card="onClickArticleCard(article, index + 1)"
          @clicked-on-category="onClickCategory"
          @click-on-restore-button="updateViewsCount(article.id)"
          @favorite-delete="(favorite) => onFavoriteDelete(favorite, article)"
          @favorite-create-folder-and-add="
            (nameFolder) => onFavoriteCreateFolderAndAdd(nameFolder, article)
          "
          @favorite-create-folder-and-transfer="
            (nameFolder, favorite) =>
              onFavoriteCreateFolderAndTransfer(nameFolder, favorite, article, false)
          "
          @favorite-transfer-to-folder="
            (favorite, folder) => onFavoriteTransferToFolder(favorite, folder, article, false)
          "
          @favorite-add-to-folder="(folder) => onFavoriteAddToFolder(folder, article)"
          @favorite-deferred="onFavoriteDeferred"
        />
      </div>

      <ArticleCardSmallPlaceholder v-if="status === 'pending'" />

      <IntersectionObserver
        v-if="!done && status === 'success'"
        ref="observer"
        @appear="loadMore"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
  import ArticleCardSmall from '@/components/lh-ui/ArticleCard/ArticleCardSmall/ArticleCardSmall.vue';

  import IntersectionObserver from '@/components/lh-ui/IntersectionObserver/IntersectionObserver.vue';
  import { useArticleProps } from '~/composables/useArticleProps';
  import { mapProfilePost } from '~/libs/mapProfilePost';
  import { useFavorite } from '~/composables/useFavoriteMethods';
  import { useUpdatingViewsCount } from '~/composables/useUpdatingViewsCount';
  import { getFullUrl } from '~/utils';
  import { useArticlesStore } from '~/store/articles';
  import type { IArticle } from '~/types/typings';
  import { handleApiError } from '@devhacker/shared/utils/handleApiError';
  import { fetchPublicUserPosts } from '@devhacker/shared/features/profile/api';

  const ArticleCardSmallPlaceholder = defineAsyncComponent(
    () => import('~/placeholders/ArticleCardSmallPlaceholder/ArticleCardSmallPlaceholder.vue'),
  );
  const BannerNoData = defineAsyncComponent(
    () => import('~/components/BannerNoData/BannerNoData.vue'),
  );

  const props = defineProps({
    userId: {
      type: String as PropType<string>,
      required: true,
    },
    authorName: {
      type: String as PropType<string | null>,
      default: null,
    },
    isOwnProfile: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    isAuthor: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    eventCategory: {
      type: String as PropType<string>,
      required: true,
    },
  });

  const { runWithContext } = useNuxtApp();
  const articlesStore = useArticlesStore();
  const { getLoadingViews, updateViewsCount } = useUpdatingViewsCount();
  const {
    onFavoriteDelete,
    onFavoriteDeferred,
    onFavoriteAddToFolder,
    onFavoriteTransferToFolder,
    onFavoriteCreateFolderAndAdd,
    onFavoriteCreateFolderAndTransfer,
  } = useFavorite();

  type ProfilePostsResponse = {
    data: Array<IArticle & { images?: IArticle['img'] }>;
    meta?: { total?: number };
  };

  const POSTS_COUNT_ON_PAGE = 30;
  const page = ref(1);
  const total = ref<number>(0);
  const items = ref<IArticle[]>([]);
  const done = computed(() =>
    total.value !== undefined ? items.value.length >= total.value : true,
  );

  const { data: newPostsData, status } = await useAsyncData(
    `profile_posts_${props.userId}_${page.value}`,
    async () => {
      try {
        return await fetchPublicUserPosts<ProfilePostsResponse>(props.userId, {
          page: page.value,
          limit: POSTS_COUNT_ON_PAGE,
        });
      } catch (error) {
        return handleApiError(error, runWithContext);
      }
    },
    {
      watch: [page],
    },
  );

  watch(
    newPostsData,
    (newPostsData) => {
      if (!newPostsData) return;

      items.value.push(...newPostsData.data.map(mapProfilePost));
      total.value = newPostsData.meta!.total as number;

      if (import.meta.server) return;
      const ids = newPostsData.data.map((elem) => elem.id);
      articlesStore.fetchCommentsCount(ids);
      articlesStore.fetchViewsCount(ids);
      articlesStore.fetchAndSetFavorites({ post_ids: ids.join() });
    },
    { immediate: true },
  );

  function loadMore() {
    page.value++;
  }

  const onClickArticleCard = (article: IArticle, order: number) => {
    useNuxtApp().$sendYandexMetrika({
      level1: 'Публичный профиль автора_выбор материала',
      level4: article.url,
      level5: order,
      level8: article.teaserUrl ? 'Тизер' : 'Списочная карточка',
    });
  };

  const onClickCategory = (link: string, titleCategory: string, _title: string) => {
    useNuxtApp().$sendYandexMetrika({
      level1: 'Выбор рубрики',
      level4: getFullUrl(link),
      level5: titleCategory,
      level6: 'unknown',
    });
  };
</script>

<style lang="scss" scoped>
  .posts {
    width: 100%;
    display: flex;

    &__no-data {
      width: 100%;
      padding: 40px 0;
      text-align: center;

      @include tablet {
        padding: 80px 0;
      }
    }

    &__list {
      width: 100%;
    }

    &__item {
      $padding: 16px;
      $paddingTablet: 24px;

      position: relative;

      @include hover {
        z-index: 4;
      }

      &:not(:last-child)::after {
        position: absolute;
        left: $padding;
        bottom: 0;

        height: 1px;
        width: calc(100% - (#{$padding * 2}));

        background: $borderColor;
        content: '';

        @include tablet {
          left: $paddingTablet;

          width: calc(100% - (#{$paddingTablet} * 2));
        }
      }
    }
  }
</style>
